import { useEffect, useState, useRef, useCallback } from "react";
import { MessageCircle, X, Headset } from "lucide-react";
import api from "@/lib/api";
import { postSupportMessage } from "@/lib/support";
import SupportComposer from "@/components/SupportComposer";
import SupportImage from "@/components/SupportImage";
import { App } from "@capacitor/app";
import { isNative } from "@/lib/mobile";

export function ChatWidget({ aboveCoinActions = false }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const endRef = useRef(null);
  const [visible, setVisible] = useState(document.visibilityState !== "hidden");
  const mounted = useRef(false);
  const reading = useRef(new Set());
  const requestVersion = useRef(0);
  const appActive = useRef(true);
  const fetching = useRef(false);

  const load = useCallback(async () => {
    if (fetching.current) return;
    fetching.current = true;
    const version = ++requestVersion.current;
    try {
      const { data } = await api.get("/chat/messages");
      if (!mounted.current || version !== requestVersion.current) return;
      setMessages(data.messages);
      setUnread(data.unread_count || 0);
    } catch {}
    finally { fetching.current = false; }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    const id = setInterval(load, 4000);
    const visibility = () => {
      const active = appActive.current && document.visibilityState !== "hidden";
      setVisible(active);
      if (active) load();
    };
    document.addEventListener("visibilitychange", visibility);
    const listener = isNative ? App.addListener("appStateChange", ({ isActive }) => {
      appActive.current = isActive;
      visibility();
    }) : null;
    return () => {
      mounted.current = false;
      clearInterval(id);
      document.removeEventListener("visibilitychange", visibility);
      listener?.then((handle) => handle.remove());
    };
  }, [load]);

  useEffect(() => {
    if (!open || !visible) return;
    endRef.current?.scrollIntoView?.({ behavior: "smooth" });
    const ids = messages.filter((m) => m.sender === "admin" && !m.read_by_user && !reading.current.has(m.id)).map((m) => m.id);
    if (!ids.length) return;
    ids.forEach((id) => reading.current.add(id));
    api.post("/chat/messages/read", { message_ids: ids }).then(({ data }) => {
      if (!mounted.current) return;
      ++requestVersion.current;
      setMessages((previous) => previous.map((m) => ids.includes(m.id) ? { ...m, read_by_user: true } : m));
      setUnread(data.unread_count || 0);
    }).catch(() => {
      // Failed acknowledgements retain their unread state and retry on polling.
    }).finally(() => ids.forEach((id) => reading.current.delete(id)));
  }, [open, visible, messages]);

  const send = async (text, file) => {
    const { data } = await postSupportMessage("/chat/messages", text, file);
    setMessages((prev) => [...prev, data.message]);
  };

  return (
    <>
      {!open && (
        <button
          data-testid="open-chat-btn"
          aria-label={`Open support chat${unread ? `, ${unread} unread messages` : ""}`}
          onClick={() => setOpen(true)}
          className={`support-chat-launcher ${aboveCoinActions ? "support-chat-above-actions" : ""} fixed right-4 z-[60] w-14 h-14 rounded-full bg-[#00E676] text-black flex items-center justify-center shadow-lg shadow-[#00E676]/30`}
        >
          <MessageCircle size={24} strokeWidth={2.4} />
          {unread > 0 && (
            <span data-testid="chat-unread-count" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center">
              {unread}
            </span>
          )}
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-md h-[75vh] sm:h-[560px] bg-[#0B0E14] border border-[#1E293B] rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="chat-panel"
          >
            <div className="flex items-center gap-2 px-4 h-14 border-b border-[#1E293B] bg-[#121721]">
              <div className="w-8 h-8 rounded-full bg-[#00E676]/15 flex items-center justify-center">
                <Headset size={17} className="text-[#00E676]" />
              </div>
              <div>
                <div className="text-sm font-semibold">Support</div>
                <div className="text-[10px] text-[#00E676] flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" /> Live chat
                </div>
              </div>
              <button data-testid="close-chat-btn" onClick={() => setOpen(false)} className="ml-auto text-slate-400">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-center text-xs text-slate-500 pt-8">
                  Send us a message â€” our team replies here anytime.
                </div>
              )}
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm ${
                      m.sender === "user"
                        ? "bg-[#00E676] text-black rounded-br-sm"
                        : "bg-[#1E2330] text-slate-100 rounded-bl-sm"
                    }`}
                  >
                    {m.image && <SupportImage image={m.image} />}
                    {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                    <div className={`text-[9px] mt-0.5 ${m.sender === "user" ? "text-black/50" : "text-slate-500"}`}>
                      {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <SupportComposer onSend={send} />
          </div>
        </div>
      )}
    </>
  );
}
